// These enums must match those in drumkit/common/models/integration.
export enum TMS {
  Aljex = 'aljex',
  Turvo = 'turvo',
  FreightFlow = 'freightflow',
  Relay = 'relay',
  Tai = 'tai',
  McleodEnterprise = 'mcleodenterprise',
  Mcleod = 'mcleod',
  GlobalTranzTMS = 'globaltranztms',
  ThreeG = 'threeg',
  Revenova = 'revenova',
  QuantumEdge = 'quantumedge',
  Stark = 'stark',
  TransportPro = 'transportpro',
  MercurygateBoomi = 'mercurygate-boomi',
  PLS = 'pls',
}

type TMSTimezoneConfig = {
  isLoadTimezoneAgnostic: boolean;
  isTrackAndTraceTimezoneAgnostic: boolean;
};

const tmsTimezoneAwareConfig: TMSTimezoneConfig = {
  isLoadTimezoneAgnostic: false,
  isTrackAndTraceTimezoneAgnostic: false,
};

export const TMSTimezoneConfig: Record<TMS, TMSTimezoneConfig> = {
  [TMS.Aljex]: {
    isLoadTimezoneAgnostic: true,
    isTrackAndTraceTimezoneAgnostic: true,
  },
  [TMS.Tai]: {
    isLoadTimezoneAgnostic: true,
    isTrackAndTraceTimezoneAgnostic: true,
  },
  [TMS.QuantumEdge]: {
    isLoadTimezoneAgnostic: true,
    isTrackAndTraceTimezoneAgnostic: false,
  },
  [TMS.Relay]: {
    isLoadTimezoneAgnostic: false,
    isTrackAndTraceTimezoneAgnostic: true,
  },
  [TMS.MercurygateBoomi]: {
    isLoadTimezoneAgnostic: true,
    isTrackAndTraceTimezoneAgnostic: true,
  },
  [TMS.PLS]: {
    isLoadTimezoneAgnostic: true,
    isTrackAndTraceTimezoneAgnostic: true,
  },

  // All others
  [TMS.Turvo]: tmsTimezoneAwareConfig,
  [TMS.FreightFlow]: tmsTimezoneAwareConfig,
  [TMS.McleodEnterprise]: tmsTimezoneAwareConfig,
  [TMS.Mcleod]: tmsTimezoneAwareConfig,
  [TMS.GlobalTranzTMS]: tmsTimezoneAwareConfig,
  [TMS.ThreeG]: tmsTimezoneAwareConfig,
  [TMS.Revenova]: tmsTimezoneAwareConfig,
  [TMS.Stark]: tmsTimezoneAwareConfig,
  [TMS.TransportPro]: tmsTimezoneAwareConfig,
};

export enum Quoting {
  DAT = 'dat',
  KencoFlash = 'kencoflash',
  Greenscreens = 'greenscreens',
  GlobalTranz = 'globaltranz',
  TruckStop = 'truckstop',
  Tai = 'tai',
  Sonar = 'sonar',
  Transfix = 'transfix',
  SuperDispatch = 'superdispatch',
  KitCoach = 'kitcoach',
  /** TQL-only FTL Gateway pricing */
  TruPay = 'trupay',
}

export enum QuotingPortals {
  FreightView = 'freightview',
  E2Open = 'e2open-quoting', // Different from TMS integration
  UberFreight = 'uberfreight',
  Shipwell = 'shipwell',
  NavisphereCarrier = 'navisphere-carrier',
  CarrierPoint = 'carrierpoint',
  OTM = 'otm-quoting',
  CarrierDesk = 'carrierdesk',
  ShipperGuide = 'shipperguide',
  Transporeon = 'transporeon',
  EmergeMarket = 'emergemarket',
  TruckerTools = 'truckertools',
  Manhattan = 'manhattan-quoting', // Different from scheduling integration
  InsightTMS = 'insight-tms',
  MercuryGate = 'mercurygate-quoting',
  PrincetonTmx = 'princeton-tmx',
  Kewill = 'kewill',
}

/** Frontend-owned SharePoint sources. Graph template sources are backend strings. */
export enum SharepointLoadBuildingSources {
  Agile = 'sharepoint-agile',
}

export enum CarrierVerification {
  MyCarrierPortal = 'mycarrierportal',
  Highway = 'highway',
  FreightValidate = 'freightvalidate',
  Truckstop = 'truckstop',
}

// Map enums to their human-readable names
export const integrationNameMap: Record<
  TMS | Quoting | QuotingPortals,
  string
> = {
  [Quoting.DAT]: 'DAT',
  [Quoting.KencoFlash]: 'Kenco Flash',
  [Quoting.Greenscreens]: 'Greenscreens',
  [Quoting.GlobalTranz]: 'GlobalTranz',
  [Quoting.TruckStop]: 'Truckstop',
  [Quoting.Sonar]: 'SONAR',
  [Quoting.Transfix]: 'Transfix',
  [Quoting.SuperDispatch]: 'Super Dispatch',
  [Quoting.KitCoach]: 'Kit Coach',
  [Quoting.TruPay]: 'TruPay',
  [TMS.Aljex]: 'Aljex',
  [TMS.Turvo]: 'Turvo',
  [TMS.ThreeG]: '3G',
  [TMS.FreightFlow]: 'FreightFlow',
  [TMS.Relay]: 'Relay',
  [TMS.Tai]: 'Tai',
  // Workaround to clarify on FE that this data is customer-specific. Trifecta our only GlobalTranzTMS customer at the moment.
  [TMS.GlobalTranzTMS]: 'Trifecta',
  // Typically customers have Mcleod Enterprise or Mcleod Innovation so we can use "Mcleod" for both keep user-facing name concise
  [TMS.McleodEnterprise]: 'McLeod',
  [TMS.Mcleod]: 'McLeod',
  [TMS.Revenova]: 'Revenova',
  [TMS.QuantumEdge]: 'QuantumEdge',
  [TMS.Stark]: 'Stark',
  [TMS.TransportPro]: 'TransportPro',
  [TMS.MercurygateBoomi]: 'MercuryGate',
  [TMS.PLS]: 'PLS',
  [QuotingPortals.FreightView]: 'FreightView',
  [QuotingPortals.E2Open]: 'E2Open',
  [QuotingPortals.UberFreight]: 'UberFreight',
  [QuotingPortals.Shipwell]: 'Shipwell',
  [QuotingPortals.NavisphereCarrier]: 'Navisphere Carrier',
  [QuotingPortals.CarrierPoint]: 'CarrierPoint',
  [QuotingPortals.OTM]: 'OTM',
  [QuotingPortals.CarrierDesk]: 'CarrierDesk',
  [QuotingPortals.ShipperGuide]: 'ShipperGuide',
  [QuotingPortals.Transporeon]: 'Transporeon',
  [QuotingPortals.EmergeMarket]: 'Emerge Market',
  [QuotingPortals.TruckerTools]: 'Trucker Tools',
  [QuotingPortals.Manhattan]: 'Manhattan',
  [QuotingPortals.InsightTMS]: 'Insight TMS',
  [QuotingPortals.MercuryGate]: 'MercuryGate',
  [QuotingPortals.PrincetonTmx]: 'Princeton TMX',
  [QuotingPortals.Kewill]: 'Kewill',
};

export enum IntegrationType {
  Quoting = 'pricing',
  TMS = 'tms',
  Scheduling = 'scheduling',
  CarrierVerification = 'carrierverification',
  Accessorial = 'accessorial',
}

export enum AccessorialIntegration {
  Shiplify = 'shiplify',
}

export enum IntegrationSubType {
  FTL = 'ftl',
  LTL = 'ltl',
  Hybrid = 'hybrid',
}
